'use client'

import { useEffect, useRef, useState } from 'react'

export interface JumpNavItem {
    /** The target section's element id. */
    id: string
    label: string
}

const PILL =
    'inline-flex min-h-10 items-center rounded-full border px-3.5 text-[15px] font-semibold whitespace-nowrap no-underline transition-colors duration-150 motion-reduce:transition-none'

/**
 * A venue guide's primary navigation. Readers land from search with one
 * question, so the jump links are the way in, not a courtesy.
 *
 * The inline pill row sits above the hero and is plain anchors, so it works
 * with no JS. Once that row scrolls out of view, a compact pill strip pins to
 * the top of the viewport, highlights the section in view, and keeps the
 * active pill centered as the reader scrolls. Section ids must match `items`.
 */
export function JumpNav({ items }: { items: readonly JumpNavItem[] }) {
    const inlineRef = useRef<HTMLElement>(null)
    const stripRef = useRef<HTMLElement>(null)
    const [pinned, setPinned] = useState(false)
    const [active, setActive] = useState<string | null>(null)

    // Show the strip once the inline row is fully above the viewport.
    useEffect(() => {
        const node = inlineRef.current
        if (!node) {
            return
        }
        const observer = new IntersectionObserver(([entry]) => {
            setPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0)
        })
        observer.observe(node)
        return () => observer.disconnect()
    }, [])

    // The active section is the last one whose top has passed the strip.
    useEffect(() => {
        let frame = 0
        const update = () => {
            frame = 0
            let current: string | null = null
            for (const item of items) {
                const el = document.getElementById(item.id)
                if (el && el.getBoundingClientRect().top < 140) {
                    current = item.id
                }
            }
            setActive(current)
        }
        const onScroll = () => {
            if (!frame) {
                frame = requestAnimationFrame(update)
            }
        }
        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', onScroll)
            if (frame) {
                cancelAnimationFrame(frame)
            }
        }
    }, [items])

    // Keep the active pill centered in the strip.
    useEffect(() => {
        const strip = stripRef.current
        if (!strip || !pinned || !active) {
            return
        }
        const pill = strip.querySelector<HTMLElement>(`[data-id="${active}"]`)
        if (!pill) {
            return
        }
        const left = pill.offsetLeft - (strip.clientWidth - pill.offsetWidth) / 2
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        strip.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' })
    }, [active, pinned])

    return (
        <>
            <nav
                ref={inlineRef}
                aria-label="Jump to"
                className="mb-7 flex flex-wrap justify-center gap-2"
            >
                {items.map((item) => (
                    <a
                        key={item.id}
                        href={`#${item.id}`}
                        className={`${PILL} border-[var(--smoke-300)] bg-white text-[var(--wanderblu-700)] hover:bg-[var(--wanderblu-50)]`}
                    >
                        {item.label}
                    </a>
                ))}
            </nav>

            <div
                aria-hidden={!pinned}
                className={`fixed inset-x-0 top-0 z-30 border-b border-[var(--smoke-300)] bg-white shadow-[0_2px_6px_rgba(46,48,51,0.08)] transition-transform duration-150 motion-reduce:transition-none ${pinned ? 'translate-y-0' : 'pointer-events-none -translate-y-full'}`}
            >
                <nav
                    ref={stripRef}
                    aria-label="Jump to"
                    className="mx-auto flex max-w-5xl justify-center-safe gap-1.5 overflow-x-auto px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    {items.map((item) => {
                        const on = item.id === active
                        return (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                data-id={item.id}
                                tabIndex={pinned ? undefined : -1}
                                aria-current={on ? 'location' : undefined}
                                className={`${PILL} min-h-9 flex-none px-3 text-[14px] ${
                                    on
                                        ? 'border-[var(--wanderblu-700)] bg-[var(--wanderblu-700)] text-white'
                                        : 'border-[var(--smoke-300)] bg-white text-[var(--wanderblu-700)]'
                                }`}
                            >
                                {item.label}
                            </a>
                        )
                    })}
                </nav>
            </div>
        </>
    )
}
